"use client";

import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Label } from "@/app/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { Palette, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/app/components/ui/copy-button";

export default function ColorPaletteGeneratorTool() {
  const [paletteType, setPaletteType] = useState("random");
  const [colorCount, setColorCount] = useState(5);
  const [generatedColors, setGeneratedColors] = useState<string[]>([]);

  const generateRandomColor = () => {
    return "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
  };

  const generatePalette = () => {
    const colors: string[] = [];
    
    if (paletteType === "random") {
      for (let i = 0; i < colorCount; i++) {
        colors.push(generateRandomColor());
      }
    } else if (paletteType === "monochromatic") {
      const baseHue = Math.floor(Math.random() * 360);
      for (let i = 0; i < colorCount; i++) {
        const saturation = 50 + (i * 10);
        const lightness = 30 + (i * 15);
        colors.push(`hsl(${baseHue}, ${saturation}%, ${lightness}%)`);
      }
    } else if (paletteType === "complementary") {
      const baseHue = Math.floor(Math.random() * 360);
      const complementaryHue = (baseHue + 180) % 360;
      
      for (let i = 0; i < colorCount; i++) {
        if (i % 2 === 0) {
          colors.push(`hsl(${baseHue}, 70%, ${50 + i * 10}%)`);
        } else {
          colors.push(`hsl(${complementaryHue}, 70%, ${50 + i * 10}%)`);
        }
      }
    } else if (paletteType === "analogous") {
      const baseHue = Math.floor(Math.random() * 360);
      for (let i = 0; i < colorCount; i++) {
        const hue = (baseHue + i * 30) % 360;
        colors.push(`hsl(${hue}, 70%, 50%)`);
      }
    }
    
    setGeneratedColors(colors);
    toast.success("Color palette generated successfully!");
  };

  const clearAll = () => {
    setGeneratedColors([]);
    toast.info("Colors cleared");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Color Palette Generator
            </CardTitle>
            <CardDescription>
              Generate beautiful color palettes for design
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Palette Type</Label>
              <Select value={paletteType} onValueChange={setPaletteType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Random</SelectItem>
                  <SelectItem value="monochromatic">Monochromatic</SelectItem>
                  <SelectItem value="complementary">Complementary</SelectItem>
                  <SelectItem value="analogous">Analogous</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <QuantitySlider
              label="Number of Colors"
              value={colorCount}
              min={2}
              max={10}
              step={1}
              onValueChange={setColorCount}
              hint="2–10 colors"
            />

            <div className="flex gap-2">
              <Button onClick={generatePalette}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate Palette
              </Button>
              <Button onClick={clearAll} variant="outline">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Generated Palette</CardTitle>
            <CardDescription>
              Your color palette will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {generatedColors.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {generatedColors.map((color, index) => (
                    <div key={index} className="space-y-2">
                      <div 
                        className="w-full h-16 rounded border"
                        style={{ backgroundColor: color }}
                      />
                      <div className="text-xs font-mono text-center">
                        {color}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Palette className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Click "Generate Palette" to see colors here</p>
              </div>
            )}

            {generatedColors.length > 0 && (
              <CopyButton
                text={generatedColors.join("\n")}
                variant="default"
                size="default"
                className="w-full"
                successMessage="Colors copied!"
              >
                Copy All Colors
              </CopyButton>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Color Palette Types</CardTitle>
          <CardDescription>
            Learn about different color harmony types
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Random</div>
              <div className="text-sm text-muted-foreground">Completely random colors</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Monochromatic</div>
              <div className="text-sm text-muted-foreground">Same hue, different saturation/lightness</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Complementary</div>
              <div className="text-sm text-muted-foreground">Opposite colors on color wheel</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Analogous</div>
              <div className="text-sm text-muted-foreground">Adjacent colors on color wheel</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
